import {ActionFunctionArgs, LoaderFunctionArgs, redirect} from '@remix-run/node';
import {getBuilderSearchParams, isEditing, isPreviewing} from '@builder.io/sdk-react';
import {getContent, pickVariant} from '~/builder-service';
import {z} from 'zod';
import {db} from 'shared/utils/db.server';
import {superjson, useSuperLoaderData, useSuperRouteLoaderData} from 'shared/utils/data';
import {authenticate} from '~/services/auth.server';
import {parseWithZod} from '@conform-to/zod';
import OptInForm from '~/routes/promotions.$promotionId.$partnerSlug/OptInForm';
import {getPartnerFromSlug} from '~/services/partner-service.server';
import {Container, Flex, Stack, VStack} from '@styled-system/jsx';
import {GiveawayDetailsProps, loader as parentLoader} from '~/routes/promotions.$promotionId/route';
import {css} from '@styled-system/css';
import {useState} from 'react';
import PromotionHeader from '~/components/PromotionHeader';
import {handleOptIn} from '~/services/journey-service.server';

export const FANATHEM_PARTNER_ID = '2fc51d11-8f36-4f6f-8d69-a60a2e071443';

type BuilderOptInFormData = {
  giveawayId: string
  partnerIds?: string[]
  products: {
    productId: string
    minimumMembershipMultiplier?: number
    splashImage?: string
  }[]
  upsells: {
    page: string
  }[]
  tags: string[]
}

export const optInSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  phone: z.string(),
  consent: z.boolean().optional(),
  productId: z.string().uuid(),
  landingPage: z.string(),
  upsells: z.string().transform<{ page: string }[]>(s => JSON.parse(s)), // TODO: Make this better
  params: z.string().optional().transform<{ [k in string]: string }>(s => s ? JSON.parse(s) : {}),
  tags: z.string().optional().transform<string[]>(s => s && JSON.parse(s)), // TODO: Make this better?
  referrer: z.string().optional(),
})

export const loader = async ({request, params}: LoaderFunctionArgs) => {
  const {promotionId, partnerSlug} = params;

  if (!promotionId || !partnerSlug) {
    throw new Error();
  }

  const promotion = await db.giveaway.findUniqueOrThrow({
    where: {
      id: promotionId,
    },
    select: {
      details: {
        select: {
          status: true
        }
      }
    }
  });

  const partner = await getPartnerFromSlug!(partnerSlug);

  const searchParams = new URL(request.url).searchParams;
  const isEditingOrPreviewing = isEditing(searchParams) || isPreviewing(searchParams);

  if (!isEditingOrPreviewing && (!partner || promotion.details?.status == 'ENDED')) {
    throw redirect(`/promotions/${promotionId}`);
  }

  const builderContent = await getContent({
    model: 'opt-in-form',
    options: getBuilderSearchParams(searchParams),
    query: {
      data: {
        giveawayId: promotionId
      }
    },
    userAttributes: {
      giveawayId: promotionId,
      partnerId: partner?.id,
    },
  });

  const optInData = pickVariant(builderContent)?.data as BuilderOptInFormData | null;

  const loggedInCustomer = await authenticate(request);

  const customer = loggedInCustomer ? await db.customer.findUnique({
    where: {
      id: loggedInCustomer.customerId
    },
    select: {
      firstName: true,
      lastName: true,
      emailAddress: true,
      phoneNumber: true,
    }
  }) : null;

  // TODO This is wrong
  const activeSubscription = loggedInCustomer ? await db.userSubscription.findFirst({
    where: {
      customerId: loggedInCustomer.customerId,
    },
    select: {
      subscription: {
        select: {
          additionalEntryMultiplier: true
        }
      }
    }
  }) : null;

  const allProducts = optInData?.products ?? [];
  const filteredProducts = allProducts.filter(p => {
    return isEditingOrPreviewing || (p.minimumMembershipMultiplier ?? 0) <= (activeSubscription?.subscription?.additionalEntryMultiplier ?? 0)
  })

  const products = await db.productSummaryDetails.findMany({
    where: {
      productId: {
        in: filteredProducts.map(p => p.productId)
      }
    },
    select: {
      productId: true,
      title: true,
      imageUrl: true,
      price: true,
      shippingPrice: true
    }
  });

  return superjson({
    partner: partner?.id !== FANATHEM_PARTNER_ID ? partner : undefined,
    products: filteredProducts
      .map(fp => {
        const product = products.find(p => p.productId === fp.productId);
        return product && ({
          ...product,
          splashImage: fp.splashImage ?? product.imageUrl
        });
      })
      .filter(p => p !== undefined),
    customer,
    builderContent: optInData,
    hasFilteredProducts: allProducts.length !== filteredProducts.length,
  });
}

export const action = async ({request, params}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const submission = parseWithZod(formData, {schema: optInSchema});

  if (submission.status !== 'success') {
    return submission.reply();
  }

  const {promotionId, partnerSlug} = params;

  if (!promotionId || !partnerSlug) {
    throw new Error();
  }

  const partner = await getPartnerFromSlug!(partnerSlug)

  if (!partner) {
    throw new Error();
  }

  await handleOptIn(request.url, {
    promotionId,
    consent: submission.value.consent ?? false,
    email: submission.value.email,
    firstName: submission.value.firstName,
    landingPage: submission.value.landingPage,
    lastName: submission.value.lastName,
    phone: submission.value.phone,
    productId: submission.value.productId,
    referrer: submission.value.referrer,
    tags: submission.value.tags,
    upsells: submission.value.upsells,
    partner,
    params: submission.value.params,
  })
}

const PromotionPartnerPage = () => {
  const {builderContent, customer, partner, products, hasFilteredProducts} = useSuperLoaderData<typeof loader>();
  const {promotion, builderContent: parentBuilderContent} = useSuperRouteLoaderData<typeof parentLoader>('routes/promotions.$promotionId')

  const parentBuilderData = parentBuilderContent?.data as GiveawayDetailsProps | undefined;

  const [productImage, setProductImage] = useState<string | undefined>(undefined);

  return <VStack>
    <Container width="full">
      <Stack
        justifyContent="space-between"
        alignItems={{base: 'stretch', sm: 'flex-start'}}
        direction={{base: 'column', sm: 'row'}}
      >
        <PromotionHeader
          title={promotion.title}
          startDate={promotion.startDate}
          endDate={promotion.endDate}
          entryLimit={parentBuilderData?.limitedEntries}
        />
      </Stack>
    </Container>
    <Container width="full">
      <Stack
        justifyContent="space-evenly"
        alignItems={{base: 'stretch', sm: 'flex-start'}}
        direction={{base: 'column', sm: 'row'}}
        gap={8}
      >
        {promotion.imageUrl && (
          <Flex alignItems="center" justifyContent="center">
            <img src={parentBuilderData?.splashImage ?? promotion.imageUrl} className={css({maxH: '400px'})} alt=""/>
          </Flex>
        )}
        {productImage && (
          <Flex alignItems="center" justifyContent="center">
            <img src={productImage} className={css({maxH: '400px'})} alt=""/>
          </Flex>
        )}
      </Stack>
    </Container>
    <Container width="full">
      <OptInForm
        partnerName={partner?.name}
        products={products}
        upsells={builderContent?.upsells ?? []}
        customer={customer ?? undefined}
        tags={[...(builderContent?.tags ?? []), ...(parentBuilderData?.tags ?? [])]}
        hasFilteredProducts={hasFilteredProducts}
        setProductImage={setProductImage}
      />
    </Container>
  </VStack>
}

export default PromotionPartnerPage;
